import { type FC, Fragment, useState } from "react";
import { Link } from "react-router-dom";
import {
  Chip,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import type { MenuSectionProps } from "@/admin/shared/constants";
import { useLocation } from "react-router";

export interface MultiLevelMenuProps {
  sections: MenuSectionProps[];
  /** Счётчики у пунктов по item.to; ноль и отсутствие не рисуются. */
  badges?: Record<string, number>;
}

const ADMIN_SIDEBAR_EXPANDED_KEY = "admin-sidebar-expanded";

export const MultiLevelMenu: FC<MultiLevelMenuProps> = ({
  sections,
  badges = {},
}) => {
  const getInitialExpanded = () => {
    const saved = localStorage.getItem(ADMIN_SIDEBAR_EXPANDED_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        localStorage.removeItem(ADMIN_SIDEBAR_EXPANDED_KEY);
      }
    }
    return {};
  };

  const [expanded, setExpanded] =
    useState<Record<string, boolean>>(getInitialExpanded());

  const toggleSection = (section: string) => {
    setExpanded((prev) => {
      const newExpanded = {
        ...prev,
        [section]: !prev[section],
      };
      // Сохраняем состояние в localStorage
      localStorage.setItem(
        ADMIN_SIDEBAR_EXPANDED_KEY,
        JSON.stringify(newExpanded)
      );
      return newExpanded;
    });
  };

  const location = useLocation();

  const badge = (count: number | undefined) =>
    count && count > 0 ? (
      <Chip size="small" color="error" label={count} sx={{ ml: 1 }} />
    ) : null;

  return (
    <List
      disablePadding
      sx={{
        overflowY: "auto",
        msOverflowStyle: "none",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": {
          display: "none",
        },
      }}
    >
      {sections.map((section) => (
        <Fragment key={section.section}>
          {/* Раздел без заголовка (панель управления) всегда раскрыт. */}
          {!section.alwaysOpen && (
            <ListItemButton
              onClick={() => toggleSection(section.section)}
              sx={{ py: 0.75, px: 1.5 }}
            >
              <ListItemText
                primary={section.title}
                slotProps={{
                  primary: {
                    variant: "subtitle2",
                    fontWeight: 600,
                  },
                }}
              />
              {/* Свёрнутый раздел прячет пункты: сумма видна у заголовка. */}
              {!expanded[section.section] &&
                badge(
                  section.items.reduce(
                    (total, item) => total + (badges[item.to] ?? 0),
                    0
                  )
                )}
              {expanded[section.section] ? (
                <ExpandLessIcon color="disabled" fontSize="small" />
              ) : (
                <ExpandMoreIcon color="disabled" fontSize="small" />
              )}
            </ListItemButton>
          )}
          <Collapse
            in={section.alwaysOpen || expanded[section.section]}
            timeout="auto"
            unmountOnExit
          >
            <List disablePadding>
              {section.items.map((item) => {
                const isSelected = location.pathname === item.to;
                const Icon = item.icon;

                return (
                  <ListItem key={item.to} disablePadding>
                    <ListItemButton
                      component={Link}
                      to={item.to}
                      selected={isSelected}
                      dense
                      sx={{
                        pl: 3, // Уменьшил с 4 до 3
                        pr: 1.5,
                        py: 0.5, // Уменьшил вертикальные отступы
                        "&.Mui-selected": {
                          backgroundColor: "primary.main",
                          color: "primary.contrastText",
                          "&:hover": {
                            backgroundColor: "primary.dark",
                          },
                          "& .MuiListItemIcon-root": {
                            color: "primary.contrastText",
                          },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Icon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        slotProps={{
                          primary: {
                            variant: "body2",
                          },
                        }}
                      />
                      {badge(badges[item.to])}
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Collapse>
          <Divider />
        </Fragment>
      ))}
    </List>
  );
};
